import { Panel } from "./Panel";
import { useCexDepth } from "../hooks/useCexDepth";
import type { CexSymbol } from "../api/types";
import {
  coverageVerdict,
  imbalanceText,
  markRows,
  priceText,
  sparkPaths,
  wallVerdict,
  type Tone,
} from "../lib/cex";

/**
 * Плотность стакана биржи расчёта.
 *
 * # Зачем панель на экране
 *
 * Индекс, по которому решается окно Polymarket, считается по цене Binance. До
 * сих пор на экране была сама цена, но не то, ЧЕМ она подпёрта: сдвинуть
 * BTCUSDT на один базисный пункт стоит от $92 тыс. до $1.05 млн в зависимости
 * от минуты **[замер 2026-08-26]**, и разница в одиннадцать раз не видна нигде.
 *
 * # Чего панель НЕ утверждает
 *
 * Связи с крупными ордерами Polymarket. Она измерена, и её нет: ранговая связь
 * перекоса сторон с направлением тейкерского потока PM — `+0.007` на BTC и
 * `−0.050` на ETH при `n ≈ 2000` на актив [замер 2026-08-26, сутки локальной
 * базы]. С объёмом потока — так же ноль. Поэтому перекос стоит здесь как
 * ПОКАЗАНИЕ ПРИБОРА, а не как признак, и подписи ниже говорят про цену биржи, а
 * не про исход окна.
 *
 * Измерено и работает ровно одно — стена: односторонний всплеск плотности
 * предсказывает снос середины на 0.5–1.2 бп в следующие 30 секунд. Это на
 * порядок меньше 2.8–15.7 бп, которыми окно переворачивают, и написать над ней
 * «кит готовит переворот» было бы враньём.
 *
 * Единственная величина с воспроизведённой связью — РАЗМАХ: `+0.232` на BTC и
 * `+0.198` на ETH с объёмом тейкерского потока PM. Связь ожидаемая
 * (волатильность гонит поток на любой площадке) и стоит здесь фоном, на котором
 * читаются остальные.
 */
export function CexDepthPanel() {
  const q = useCexDepth();
  const r = q.data;

  if (!r || !r.known) {
    return (
      <Panel title="Плотность биржи расчёта" subtitle="стакан Binance" dense>
        <p className="text-xs text-[var(--color-muted)]">
          {q.isError ? "раздел не загрузился" : (r?.reason ?? "загрузка…")}
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Плотность биржи расчёта"
      subtitle="стакан Binance · накопленные доллары от середины"
      dense
    >
      <div className="flex flex-col gap-3">
        {r.symbols.map((s) => (
          <SymbolBlock key={s.symbol} s={s} />
        ))}
      </div>
      {/* Сноска обязательна и убирать её нельзя: без неё перекос и стена
          читаются как торговый сигнал по Polymarket, которым они не являются. */}
      <p className="mt-3 border-t border-[var(--color-border)] pt-2 text-[10px] leading-snug text-[var(--color-muted)]">
        Прибор, не сигнал. Связь перекоса с потоком PM измерена и её нет (r ≈ 0 на
        обоих активах). Стена предсказывает 0.5–1.2 бп цены БИРЖИ за 30 с — на
        порядок меньше того, чем переворачивают окно.
      </p>
    </Panel>
  );
}

function toneColor(tone: Tone): string {
  switch (tone) {
    case "up":
      return "var(--color-buy)";
    case "down":
      return "var(--color-sell)";
    case "unknown":
      return "var(--color-warn)";
    default:
      return "var(--color-muted)";
  }
}

function SymbolBlock({ s }: { s: CexSymbol }) {
  const rows = markRows(s.marks);
  const wall = wallVerdict(s);
  const coverage = coverageVerdict(s);
  const paths = sparkPaths(s.series, 100, 16);

  return (
    <section className="rounded border border-[var(--color-border)] p-2">
      <header className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{s.symbol}</span>
        <span className="num text-xs text-[var(--color-muted)]">
          {priceText(s.mid)}
        </span>
      </header>

      {/* Кривая: зеркальные полосы. Слева биды (подпор снизу), справа аски
          (навал сверху) — как в стакане, где биды под ценой. */}
      <div className="mt-2 flex flex-col gap-0.5">
        <div className="flex items-center gap-1 text-[10px] text-[var(--color-muted)]">
          <span className="flex-1 text-right">биды</span>
          <span className="w-8 text-center">бп</span>
          <span className="flex-1">аски</span>
        </div>
        {rows.map((row) => (
          <div key={row.bp} className="flex items-center gap-1">
            <span className="num w-12 shrink-0 text-right text-[10px] text-[var(--color-muted)]">
              {row.bidText}
            </span>
            <div className="flex flex-1 justify-end">
              <div
                className="h-2 rounded-l"
                style={{
                  width: `${row.bidShare * 100}%`,
                  // Неспрошенная отметка — штриховка, а не пустота: полоса
                  // нулевой длины у «не знаем» неотличима от «денег нет».
                  background: row.bidKnown
                    ? "var(--color-buy)"
                    : "repeating-linear-gradient(45deg, var(--color-border) 0 3px, transparent 3px 6px)",
                  minWidth: row.bidKnown ? undefined : "100%",
                  opacity: row.bidKnown ? 0.8 : 1,
                }}
              />
            </div>
            <span className="num w-8 shrink-0 text-center text-[10px]">{row.bp}</span>
            <div className="flex flex-1 justify-start">
              <div
                className="h-2 rounded-r"
                style={{
                  width: `${row.askShare * 100}%`,
                  background: row.askKnown
                    ? "var(--color-sell)"
                    : "repeating-linear-gradient(45deg, var(--color-border) 0 3px, transparent 3px 6px)",
                  minWidth: row.askKnown ? undefined : "100%",
                  opacity: row.askKnown ? 0.8 : 1,
                }}
              />
            </div>
            <span className="num w-12 shrink-0 text-[10px] text-[var(--color-muted)]">
              {row.askText}
            </span>
          </div>
        ))}
      </div>

      {/* Перекос во времени. Ноль — середина; линия рвётся на пробеле ряда. */}
      <div className="mt-2 flex items-center gap-2">
        <svg viewBox="0 0 100 16" preserveAspectRatio="none" className="h-4 flex-1">
          <line x1="0" y1="8" x2="100" y2="8" stroke="var(--color-border)" strokeWidth="0.5" />
          {paths.map((d) => (
            <path
              key={d}
              d={d}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        <span className="num w-20 shrink-0 text-right text-[10px] text-[var(--color-muted)]">
          {imbalanceText(s)}
        </span>
      </div>

      <dl className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
        <Field label="стена" value={wall.text} tone={wall.tone} />
        <Field
          label="размах 5 мин"
          value={`${s.range_bp.toFixed(1)} бп`}
          tone="flat"
        />
        <Field label="ряд" value={coverage.text} tone={coverage.tone} />
      </dl>
    </section>
  );
}

function Field({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return (
    <div className="min-w-0">
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd className="truncate" style={{ color: toneColor(tone) }} title={value}>
        {value}
      </dd>
    </div>
  );
}
